<script setup lang="ts">
import { computed, onMounted, provide, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  appConfigProviderKey,
  loadAppConfig,
} from "./infrastructure/app_config_provider";
import { loadInitContext } from "./infrastructure/initcontext_provider";
import {
  OrderApi,
  orderApiKey,
  type OrderCreateRequest,
  type OrderDto,
  type OrderResetRequest,
} from "./infrastructure/gfo_api/order_api";
import { NetzApi, netzApiKey } from "./infrastructure/gfo_api/netz_api";
import {
  PriceFormatter,
  priceFormatterKey,
} from "./infrastructure/price_formatter";
import AppWrap from "./features/AppWrap";
import {
  CurrentOrderProvider,
  currentOrderProviderKey,
} from "./infrastructure/current_order_provider";
import ErrorMessage from "./features/Shared/ErrorMessage";
import {
  optionsProviderKey,
  OptionsProvider,
} from "./infrastructure/options_provider";
import {
  configsProviderKey,
  ConfigsProvider,
} from "./infrastructure/configs_provider";
import {
  OrderService,
  orderServiceKey,
} from "./infrastructure/gfo_api/order_service";
import { WhoamiApi, whoamiApiKey } from "./infrastructure/gfo_api/whoami_api";
import {
  ProductApi,
  productApiKey,
} from "./infrastructure/gfo_api/product_api";
import { InfoApi, infoApiKey } from "./infrastructure/gfo_api/info_api";
import {
  ProductInfoProvider,
  productInfoProviderKey,
} from "./infrastructure/productinfo_provider";
import { ApiValidationError } from "./infrastructure/gfo_api/api_validation_error";

const props = defineProps<{
  netz_id?: number;
  api_token?: string;
  api_url?: string;
  cookie_name?: string;
  tracking_function_name?: string;
  on_order_change_function_name?: string;
  order_for?: string;
  checker_key_private?: string;
  checker_key_business?: string;
}>();

const initError = ref<string>("");
const initializing = ref<boolean>(true);

// Infrastructure
const router = useRouter();
const route = useRoute();
const appConfig = loadAppConfig(props);
provide(appConfigProviderKey, appConfig);
const orderApi = new OrderApi(appConfig);
provide(orderApiKey, orderApi);
const netzApi = new NetzApi(appConfig);
provide(netzApiKey, netzApi);
const whoamiApi = new WhoamiApi(appConfig);
provide(whoamiApiKey, whoamiApi);
const productApi = new ProductApi(appConfig);
provide(productApiKey, productApi);
const infoApi = new InfoApi(appConfig);
provide(infoApiKey, infoApi);

// Infrastructure
const currentOrderProvider = CurrentOrderProvider(appConfig);
provide(currentOrderProviderKey, currentOrderProvider);
provide(priceFormatterKey, PriceFormatter({ currency: false }));
provide(optionsProviderKey, OptionsProvider(currentOrderProvider));
provide(configsProviderKey, new ConfigsProvider(currentOrderProvider));
provide(productInfoProviderKey, new ProductInfoProvider(currentOrderProvider));

// Services
const orderService = new OrderService(orderApi, currentOrderProvider);
provide(orderServiceKey, orderService);

// Initialization
const initContext = loadInitContext(appConfig);

// computed
const isDev = computed(() => import.meta.env.DEV);

onMounted(async () => {
  try {
    if (appConfig.cookie_name == "") {
      throw new Error("Intialization_Cookie_Name_Undefined");
    }
    let currentOrder: OrderDto | null = null;
    if (initContext.order_import != null) {
      // Todo: Import the order (muss Serverseitig kopiert werden)
      currentOrder = await orderApi.import(initContext.order_import);
    } else if (initContext.order_reference != null) {
      // Try to load the order and check if already ordered.
      currentOrder = await orderApi.getOrderByReference(
        initContext.order_reference,
      );
    }
    if (
      currentOrder == null ||
      currentOrder.version != 4 ||
      currentOrder.canceled != null ||
      currentOrder.ordered != null ||
      currentOrder.confirmed != null
    ) {
      initContext.order_reference = null;
      currentOrder = null;
    }
    if (currentOrder == null) {
      if (initContext.netz_id != null || initContext.netz != null) {
        const request: OrderCreateRequest = {
          netz: `${initContext.netz || initContext.netz_id}`, // whatever is not null
          order_for: appConfig.order_for,
          partner_number: appConfig.partner_number,
          username: null, // TODO
          customer_number: null, //TODO
          pv_ids: initContext.pv_ids,
          checker_result_uid: initContext.checker_result_uid,
        };
        currentOrder = await orderApi.create(request);
        currentOrderProvider.set(currentOrder);
      }
    } else {
      if (
        (initContext.netz_id != null &&
          currentOrder.netz_id != initContext.netz_id) ||
        (initContext.netz != null &&
          currentOrder.netz_slug != initContext.netz) ||
        (appConfig.partner_number != null &&
          appConfig.partner_number != currentOrder.partner_number) ||
        appConfig.order_for != currentOrder.order_for
      ) {
        let newNetz = `${currentOrder.netz_id}`;
        if (initContext.netz_id != null) {
          newNetz = `${initContext.netz_id}`;
        } else if (initContext.netz != null) {
          newNetz = `${initContext.netz}`;
        }
        const request: OrderResetRequest = {
          netz: newNetz,
          order_for: appConfig.order_for ?? currentOrder.order_for,
          partner_number: appConfig.partner_number,
          username: null, // TODO
          customer_number: null, //TODO
          pv_ids: initContext.pv_ids,
          checker_result_uid: initContext.checker_result_uid,
        };
        currentOrder = await orderApi.reset(
          currentOrder.order_reference,
          request,
        );
        currentOrderProvider.set(currentOrder);
      } else {
        currentOrderProvider.set(currentOrder);
      }
    }
    if (null == currentOrder) {
      // kein Bestell-Context - Trotzdem auf Produkteseite (dort wird Checker angezeigt)
      router.push("/");
    } else {
      const variants = await orderApi.getAvailableVariants(
        currentOrder.order_reference,
      );
      currentOrderProvider?.setVariants(variants);
      const context = await orderApi.getOrderContextByReference(
        currentOrder.order_reference,
      );
      currentOrderProvider?.setOrderContext(context);
      if (currentOrder.items.length > 0) {
        router.push("/options");
      } else {
        router.push("/");
      }
    }
  } catch (err) {
    if (err instanceof ApiValidationError) {
      initError.value = err.title;
      console.log(initError.value)
    } else {
      initError.value = err instanceof Error ? err.message : String(err);
    }
  } finally {
    initializing.value = false;
  }
});
</script>

<template>
  <div v-if="initializing">Laden...</div>
  <div v-if="!initializing && initError != ''">
    <ErrorMessage :message="initError"></ErrorMessage>
  </div>

  <AppWrap v-if="!initializing && initError == ''"></AppWrap>

  <div v-if="isDev" class="mt-3 bg-orange-100 p-3">
    <div>Visible only in Dev mode</div>
    <div>Current Path: {{ route.path }}</div>
  </div>
</template>
